import { useMemo } from 'react';
import type { ChangeEvent } from 'react';

import { Button, PasswordInput } from '@parrot-software/design-system';
import { LoaderCircle, ShieldCheck } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form-v7';
import type { Resolver } from 'react-hook-form-v7';
import { useTranslation } from 'react-i18next';

import { isMagicLinkError } from '@identity/application/errors';
import type { MagicLinkErrorCode } from '@identity/application/errors';
import { isPasswordValid } from '@identity/domain/value-objects';
import { FieldLabel, PasswordRequirementsChecklist, StepCard } from '@identity/presentation/components/base';
import { createPasswordFormSchema } from '@identity/presentation/components/specific/steps';
import type { PasswordFormValues } from '@identity/presentation/components/specific/steps';
import { useCreatePassword } from '@identity/presentation/hooks';
import { resolveIdentityErrorMessage } from '@identity/presentation/utils';

export type NewPasswordViewProps = {
  onLinkError: (code: MagicLinkErrorCode) => void;
  onSaved: () => void;
};

/**
 * The screen that writes the new password.
 *
 * @param {NewPasswordViewProps} props - Component properties
 */
export const NewPasswordView = ({ onLinkError, onSaved }: NewPasswordViewProps) => {
  const { t } = useTranslation('identity');
  const { mutate: createPassword, isLoading, error, reset: resetRequest } = useCreatePassword();
  const resolver = useMemo(
    () => (yupResolver(createPasswordFormSchema()) as unknown) as Resolver<PasswordFormValues>,
    [],
  );

  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<PasswordFormValues>({
    defaultValues: { password: '' },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver,
  });

  const password = watch('password');
  const isSubmitDisabled = isLoading || !isPasswordValid(password);
  const fieldErrorKey = errors.password?.message;
  const requestErrorKey = error && !isMagicLinkError(error) ? resolveIdentityErrorMessage(error).i18nKey : undefined;

  const { onChange: onFieldChange, ...passwordField } = register('password');

  const onPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (fieldErrorKey) clearErrors('password');
    if (error) resetRequest();

    return onFieldChange(event);
  };

  const onSubmit = handleSubmit((values) =>
    createPassword(
      { password: values.password },
      {
        onSuccess: onSaved,
        onError: (failure) => {
          if (isMagicLinkError(failure)) onLinkError(failure.code);
        },
      },
    ),
  );

  return (
    <StepCard
      icon={
        <span className="flex size-8 items-center justify-center rounded-full bg-bg-info">
          <ShieldCheck aria-hidden className="size-4 text-info" />
        </span>
      }
      title={t('recovery.reset.title')}
    >
      <form className="flex flex-col gap-6 font-sans" noValidate onSubmit={onSubmit}>
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="identity-new-password" isRequired>
            {t('recovery.reset.passwordLabel')}
          </FieldLabel>
          <PasswordInput
            {...passwordField}
            aria-invalid={Boolean(fieldErrorKey)}
            aria-required
            autoComplete="new-password"
            className={cn({ 'border-destructive focus-visible:ring-destructive': Boolean(fieldErrorKey) })}
            id="identity-new-password"
            onChange={onPasswordChange}
          />
          {fieldErrorKey && <p className="text-xs text-destructive">{t(fieldErrorKey)}</p>}
          {requestErrorKey && (
            <p className="text-xs text-destructive" data-testid="new-password-request-error">
              {t(requestErrorKey)}
            </p>
          )}
        </div>

        <PasswordRequirementsChecklist value={password} />

        <Button className="w-full sm:w-fit" disabled={isSubmitDisabled} type="submit">
          {isLoading && (
            <LoaderCircle
              aria-hidden
              className="size-4 shrink-0 animate-spin"
              data-testid="new-password-submit-spinner"
            />
          )}
          {t('recovery.reset.submit')}
        </Button>
      </form>
    </StepCard>
  );
};
